import { useCallback, useEffect, useRef } from 'react';
import { useLocation, history } from 'umi';
import moment from 'moment';
import { notification } from 'tea-component';

interface Meeting {
  id: string;
  status: number; // 0: upcoming, 1: completed
  date: string;
  [key: string]: any;
}

interface UseMeetingUrlParamsProps {
  allMeetings: Meeting[];
  activeMeetingId: string | null;
  setActiveMeetingId: (id: string | null) => void;
  setDisplayedCompletedId: (id: string | null) => void;
  setDisplayedUpcomingId: (id: string | null) => void;
  hasInitializedMeetings: React.MutableRefObject<boolean>;
  /** When this URL param changes (e.g. studio_id), meeting_id is cleared and URL sync resets */
  scopeUrlParam?: string;
  /**
   * Non-URL scope (e.g. sorted selected studio ids). When it changes, clear meeting_id
   * and reset URL sync flags — without touching the click → meeting_id sync effect.
   */
  scopeKey?: string;
}


interface UseMeetingUrlParamsReturn {
  urlProcessedRef: React.MutableRefObject<boolean>;
  meetingWasFromUrl: React.MutableRefObject<boolean>;
  initializeMeetingsFromUrl: () => void;
}

/**
 * Custom hook to manage meeting selection via URL parameters
 * 
 * This hook handles:
 * 1. Reading meeting ID from URL parameter (?meeting=<id>)
 * 2. Setting initial meeting selections based on URL or defaults
 * 3. Updating URL when active meeting changes
 * 
 * @param props - Configuration object
 * @returns Object with urlProcessedRef and initialization function
 */
export const useMeetingUrlParams = ({
  allMeetings,
  activeMeetingId,
  setActiveMeetingId,
  setDisplayedCompletedId,
  setDisplayedUpcomingId,
  hasInitializedMeetings,
  scopeUrlParam,
  scopeKey,
}: UseMeetingUrlParamsProps): UseMeetingUrlParamsReturn => {
  const location = useLocation();
  const urlProcessedRef = useRef<boolean>(false);
  const prevScopeKeyRef = useRef<string | null>(null);
  
  // Track if the initial meeting came from URL (user navigated with ?meeting_id=xxx)
  // vs from default selection. Only update URL for URL-originated or user-clicked meetings.
  const meetingWasFromUrl = useRef<boolean>(false);

  /**
   * Update URL with active meeting ID
   */
  const updateUrlWithMeeting = useCallback((meetingId: string | null): void => {
    const searchParams = new URLSearchParams(location.search);
    
    if (meetingId) {
      searchParams.set('meeting_id', meetingId);
    } else {
      searchParams.delete('meeting_id');
    }
    
    const newSearch = searchParams.toString();
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    
    // Only update if URL has changed
    if (newUrl !== `${location.pathname}${location.search}`) {
      history.replace(newUrl);
    }
  }, [location.search, location.pathname]);

  /**
   * Parse meeting ID from URL parameter
   */
  const getMeetingIdFromUrl = useCallback((): string | null => {
    const searchParams = new URLSearchParams(location.search);
    const meetingParam = searchParams.get('meeting_id');
    
    return meetingParam || null;
  }, [location.search]);

  /**
   * Initialize meeting selections from URL or set defaults
   */
  const initializeMeetingsFromUrl = useCallback((): void => {
    // Skip if already initialized
    if (hasInitializedMeetings.current) {
      return;
    }

    if (allMeetings.length === 0) return;

    // Step 1: Calculate defaults for the 2 cards
    const completedMeetings = allMeetings
      .filter((item: Meeting) => item.status === 1)
      .sort((a: Meeting, b: Meeting) => moment(b.date).valueOf() - moment(a.date).valueOf());

    let defaultCompletedId: string | null = null;
    if (completedMeetings.length > 0) {
      defaultCompletedId = completedMeetings[0].id;
    }

    const upcomingMeetings = allMeetings
      .filter((item: Meeting) => item.status === 0)
      .sort((a: Meeting, b: Meeting) => moment(a.date).valueOf() - moment(b.date).valueOf());

    let defaultUpcomingId: string | null = null;
    if (upcomingMeetings.length > 0) {
      defaultUpcomingId = upcomingMeetings[0].id;
    }

    // Step 2: Determine activeMeetingId (from URL or default)
    const urlMeetingId = getMeetingIdFromUrl();
    let activeMeeting: Meeting | null = null;
    
    if (urlMeetingId && !urlProcessedRef.current) {
      console.log('[useMeetingUrlParams] Processing URL meeting ID:', urlMeetingId);
      activeMeeting = allMeetings.find((m: Meeting) => m.id === urlMeetingId) || null;
      urlProcessedRef.current = true;
      
      if (activeMeeting) {
        // Mark that meeting came from URL — should sync back to URL and trigger scroll
        meetingWasFromUrl.current = true;
      } else {
        // URL contained a meeting_id but it doesn't exist in the meetings list
        notification.error({
          description: 'Meeting Not Found',
        });
        // Clean up invalid meeting_id from URL
        updateUrlWithMeeting(null);
      }
    }
    
    // If no URL meeting or URL meeting not found, default to latest completed
    if (!activeMeeting && completedMeetings.length > 0) {
      activeMeeting = completedMeetings[0];
      if (!urlProcessedRef.current) {
        urlProcessedRef.current = true;
      }
      // Default selection — do NOT add meeting_id to URL
      meetingWasFromUrl.current = false;
    }

    // Step 3: Set activeMeetingId
    if (activeMeeting) {
      setActiveMeetingId(activeMeeting.id);
    }

    // Step 4: Update cards based on active meeting's status
    if (activeMeeting) {
      if (activeMeeting.status === 1) {
        // Active is completed - override completed card
        setDisplayedCompletedId(activeMeeting.id);
        // Keep default for upcoming card
        setDisplayedUpcomingId(defaultUpcomingId);
      } else if (activeMeeting.status === 0) {
        // Active is upcoming - override upcoming card
        setDisplayedUpcomingId(activeMeeting.id);
        // Keep default for completed card
        setDisplayedCompletedId(defaultCompletedId);
      }
    } else {
      // No active meeting, just use defaults
      setDisplayedCompletedId(defaultCompletedId);
      setDisplayedUpcomingId(defaultUpcomingId);
    }

    // Mark as initialized
    hasInitializedMeetings.current = true;
  }, [
    allMeetings,
    getMeetingIdFromUrl,
    updateUrlWithMeeting,
    setActiveMeetingId,
    setDisplayedCompletedId,
    setDisplayedUpcomingId,
    hasInitializedMeetings,
  ]);

  // When scope changes (studio filter / studio_id URL), drop stale meeting_id and reset
  // URL sync flags so the click→URL effect cannot write meeting_id back mid-fetch.
  // Does not change how meeting click updates the URL.
  useEffect(() => {
    const resolvedScopeKey = scopeKey !== undefined
      ? scopeKey
      : (scopeUrlParam
        ? new URLSearchParams(location.search).get(scopeUrlParam)
        : null);

    if (scopeKey === undefined && !scopeUrlParam) {
      return;
    }

    const prev = prevScopeKeyRef.current;
    if (prev === null) {
      // First observation — keep deep-linked meeting_id on initial load.
      prevScopeKeyRef.current = resolvedScopeKey;
      return;
    }

    if (prev === resolvedScopeKey) {
      return;
    }

    // Options bootstrap: '' → 'id1,id2' must not clear a deep-linked meeting_id.
    const isInitialPopulate = prev === '' && !!resolvedScopeKey;
    prevScopeKeyRef.current = resolvedScopeKey;
    if (isInitialPopulate) {
      return;
    }

    meetingWasFromUrl.current = false;
    urlProcessedRef.current = false;
    hasInitializedMeetings.current = false;

    const urlMeetingId = new URLSearchParams(location.search).get('meeting_id');
    if (urlMeetingId) {
      updateUrlWithMeeting(null);
    }
  }, [
    scopeKey,
    scopeUrlParam,
    location.search,
    updateUrlWithMeeting,
    hasInitializedMeetings,
  ]);

  // Update URL whenever active meeting changes
  // Only update URL if:
  // - URL has been processed (initialization complete)
  // - Meeting was from URL (user navigated with ?meeting_id=xxx) OR user clicked a meeting
  //   (urlProcessedRef is set to true by handleMeetingClick in BoardGovernanceNew)
  // This prevents adding meeting_id to URL on default selection (same as Studios behavior)
  useEffect(() => {
    if (!urlProcessedRef.current) {
      return;
    }

    if (!meetingWasFromUrl.current) {
      return;
    }

    if (!activeMeetingId) {
      updateUrlWithMeeting(null);
      return;
    }

    const urlMeetingId = getMeetingIdFromUrl();
    if (urlMeetingId === activeMeetingId) {
      return;
    }

    updateUrlWithMeeting(activeMeetingId);
  }, [activeMeetingId, location.search, getMeetingIdFromUrl, updateUrlWithMeeting]);

  return {
    urlProcessedRef,
    meetingWasFromUrl,
    initializeMeetingsFromUrl,
  };
};
